import { useMemo, type ReactNode } from "react";
import moment from "moment";
import EventDisplay from "./EventDisplay";
import DroppableCell from "./DroppableCell";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { useGridDates } from "../../../../hooks/grid/useGridDates";
import { useDragPassThrough } from "../../../../hooks/grid/useDragPassThrough";
import { useTime } from "../../../../../../../utils/hooks/time";
// import type { Event } from "../../../../types/Event";
import { hasGroupDivider } from "../../../../helpers/grid/getGroupRowStyle";
import { BASE_ROW_HEIGHT, EVENT_ROW_HEIGHT, GROUP_BY_HEIGHT, LANE_PADDING } from "../../../../constants/height";
import type { Resource } from "../../../../types/Resource";

interface CustomViewProps {
    resources: Resource[];
    events: any;
    resourceLayouts: Record<string, { totalLanes: number }>;
    eventLayouts: Record<string, { laneIndex: number }>;
    cellWidth: number;
    unassignedShifts?: boolean;
}

function CustomView({
    resources,
    events,
    resourceLayouts,
    eventLayouts,
    cellWidth,
    // unassignedShifts
}: CustomViewProps) {
    const now = useTime();
    const isDragPassThrough = useDragPassThrough();
    const { gridDates, gridStartTime, gridEndTime } = useGridDates();
    const { showCurrentTime, type } = useScheduleContext();

    const cumulativeRowTops = useMemo(() => {
        let cumulativeHeight = 0;
        return resources.reduce<Record<string, number>>((acc, resource) => {
            acc[resource.id] = cumulativeHeight;
            const layout = resourceLayouts[resource.id];
            const rowHeight = layout ? layout.totalLanes * BASE_ROW_HEIGHT : BASE_ROW_HEIGHT;
            cumulativeHeight += rowHeight;
            return acc;
        }, {});
    }, [resources, resourceLayouts]);

    if (!gridDates?.length || cellWidth <= 0) return null;

    const gridElements: ReactNode[] = [];

    // 1. Render background cells for each day
    resources.forEach((resource, resourceIndex) => {
        gridDates.forEach((cellTime, colIndex) => {
            const isCurrentTime = showCurrentTime && moment(now).isSame(moment(cellTime), "day") && ["team", "home"].includes(type);

            gridElements.push(
                <DroppableCell
                    key={`cell-${resource.id}-${cellTime}`}
                    resource={resource}
                    cellTime={cellTime}
                    gridColumnStart={colIndex + 1}
                    gridRowStart={resourceIndex + 1}
                    isCurrentTime={isCurrentTime}
                    withGroupDivider={hasGroupDivider(resources, resourceIndex)}
                // cellWidth={cellWidth}
                />
            );
        });
    });

    // 2. Render events
    events.forEach((event: any) => {
        const eventLayout = eventLayouts[event.id];
        if (!eventLayout) return;

        const resourceIndex = resources.findIndex((r) => r.id === event.resourceId);
        if (resourceIndex === -1) return;
        const viewByCountBefore = resources
            .slice(0, resourceIndex)
            .filter(r => ((resourceLayouts[r.id] as any)?.type === "view-by"))
            .length;

        const viewByOffsetPx = viewByCountBefore * (GROUP_BY_HEIGHT - 1);
        const eventStart = moment(event.startDate);
        const eventEnd = moment(event.endDate);

        // Calculate span based on days
        const startDay = eventStart.startOf("day");
        const endDay = eventEnd.clone().startOf("day");
        const visibleGridStart = moment(gridStartTime).startOf("day");
        const visibleGridEnd = moment(gridEndTime).startOf("day");

        // Check if event is within the visible grid dates
        if (eventEnd.isBefore(visibleGridStart) || eventStart.isAfter(visibleGridEnd)) return;

        const effectiveStartDay = moment.max(startDay, visibleGridStart);
        const effectiveEndDay = moment.min(endDay, visibleGridEnd);

        // Find the index of the starting day within the visible grid
        const startDayIndex = gridDates.findIndex(date => moment(date).isSame(effectiveStartDay, "day"));
        if (startDayIndex === -1) return;

        // Calculate the number of days the event spans
        const spanDays = effectiveEndDay.diff(effectiveStartDay, "days") + 1;

        const leftPx = startDayIndex * cellWidth;
        const widthPx = spanDays * cellWidth;
        if (widthPx < 0) return;

        const rowTop = cumulativeRowTops[event.resourceId];
        const laneIndex = eventLayout.laneIndex;
        const topPx = rowTop + laneIndex * (EVENT_ROW_HEIGHT + LANE_PADDING) + LANE_PADDING;
        const resource = resources?.find((item: any) => item.id === event.resourceId);

        gridElements.push(
            <EventDisplay
                key={event.id}
                event={{ ...event, resource }}
                style={{
                    position: "absolute",
                    top: `${topPx - viewByOffsetPx + viewByCountBefore}px`,
                    left: `${leftPx + 4}px`,
                    width: `${widthPx - 9}px`,
                    height: `${EVENT_ROW_HEIGHT}px`,
                    pointerEvents: isDragPassThrough(event.id) ? "none" : undefined,
                }}
            />
        );
    });

    return <>{gridElements}</>;
}

export default CustomView;